import { LoyaltyCardRankProps } from "components/molecules/LoyaltyCardRank";
import { LoyaltyTagStatusVariantT } from "components/atoms/LoyaltyTagStatusRank/types";
import {
  KEYS_REWARD_BONUS_ECOSYSTEM,
  RANK_LEVEL_LOYALTY_IMAGES,
} from "constants/loyalty";
import {
  CurrencyRewardLoyalty,
  Loyalty,
  LoyaltyRewardUser,
  LoyaltyRewardUserStatus,
  RankLoyalty,
} from "types/Loyalty";
import { StatusRank } from "components/atoms/LoyaltyRankStatus/types";
import { RewardBonusNominal, RewardFreeSpins } from "types/Reward";
import {
  RewardItemT,
  RewardNameT,
} from "components/molecules/LoyaltyListRewards/types";
import useTranslate from "../hooks/useTranslate";
import { InfoRank } from "./loyalty-rewards";
import { BoosterBackend } from "../types/Booster";
import { ResponseLoyaltyMascotInner } from "../queries/useGetLoyaltyMascots";
import {
  LoyaltyLevelRewardButtonProps,
  LoyaltyLevelRewardButtonVariant,
} from "../components/molecules/LoyaltyLevelRewardButton/types";
import { LevelBFF, RankBFF } from "../types/LoyaltyBFF";

export const formatCardsRanks = (
  userExperience: number,
  loyalty?: Loyalty,
): { cards: LoyaltyCardRankProps[]; indexCurrentRank: number } => {
  if (!loyalty) {
    return { cards: [], indexCurrentRank: 0 };
  }

  let currentRank: RankLoyalty | null = null;
  let nextRank: RankLoyalty | null = null;
  let indexCurrentRank: number = 0;

  for (let i = 0; i < loyalty.settings.ranks.length; i += 1) {
    const { requirement } = loyalty.settings.ranks[i].levels[0];

    if (userExperience >= requirement) {
      currentRank = loyalty.settings.ranks[i];
      indexCurrentRank = i;
      nextRank = loyalty.settings.ranks?.[i + 1] ?? null;
    }
  }

  if (currentRank === null) {
    // eslint-disable-next-line prefer-destructuring
    currentRank = loyalty?.settings?.ranks[0];
    indexCurrentRank = 0;
    nextRank = loyalty?.settings?.ranks?.[1] || null;
  }

  const levels: LoyaltyCardRankProps[] = currentRank.levels.map(
    (level, indexLevel, array) => {
      const rankExperience =
        indexLevel === array?.length - 1
          ? nextRank?.levels[0].requirement || level.requirement + 1
          : array[indexLevel + 1].requirement;

      return {
        userExperience,
        rankExperience,
        prevRankExperience: level.requirement,
        nameRank: currentRank?.name || "",
        isRank: false,
        level: indexLevel + 1,
        isActive:
          userExperience >= level.requirement &&
          userExperience < rankExperience,
        isFinal: indexLevel === array?.length - 1 && nextRank === null,
      };
    },
  );

  if (nextRank) {
    const nextRankCard: LoyaltyCardRankProps = {
      nameRank: nextRank?.name,
      userExperience,
      rankExperience: nextRank.levels[0].requirement,
      prevRankExperience: nextRank.levels[0].requirement,
      isRank: true,
    };

    levels.push(nextRankCard);
  }

  return { cards: levels, indexCurrentRank };
};

export const getCurrentRank = (loyalty?: Loyalty, userExperience?: number) => {
  if (!loyalty || userExperience === undefined) {
    return { currentRank: undefined, nextRank: undefined, isFinal: false };
  }

  if (userExperience === 0) {
    return {
      currentRank: loyalty?.settings?.ranks?.[0],
      nextRank: loyalty?.settings?.ranks?.[1]
        ? loyalty?.settings?.ranks?.[1]
        : undefined,
      isFinal: false,
    };
  }

  if (
    userExperience > 0 &&
    userExperience < loyalty?.settings?.ranks?.[0].levels[0].requirement
  ) {
    const currentRank = loyalty?.settings?.ranks?.[0];
    const nextRank = loyalty?.settings?.ranks?.[1];
    return {
      isFinal: false,
      currentRank,
      nextRank,
    };
  }

  let currentRank: RankLoyalty | undefined;
  let nextRank: RankLoyalty | undefined;
  let isFinal: boolean = false;

  loyalty.settings.ranks.forEach((rankLoyalty, indexRank) => {
    rankLoyalty.levels.forEach((level) => {
      if (userExperience >= level.requirement) {
        currentRank = rankLoyalty;
        nextRank = loyalty.settings.ranks?.[indexRank + 1];
        isFinal = indexRank === loyalty.settings.ranks.length - 1;
      }
    });
  });

  return { currentRank, nextRank, isFinal };
};

type getProgressOnRankResult = {
  progressRankPercent: number;
  maxExperienceRank: number | null;
};

export const getProgressOnRank = (
  loyalty?: Loyalty,
  userExperience?: number,
): getProgressOnRankResult => {
  if (!loyalty || !userExperience) {
    return {
      progressRankPercent: 0,
      maxExperienceRank: null,
    };
  }
  const countRanks = loyalty.settings.ranks.length;
  const firstRequirement = loyalty.settings.ranks[0].levels[0].requirement;
  const countLevelsLastRank =
    loyalty.settings.ranks?.[countRanks - 1].levels.length;
  const requirementLastRankLastLevel =
    loyalty.settings.ranks?.[countRanks - 1].levels?.[countLevelsLastRank - 1]
      .requirement;

  if (userExperience < firstRequirement) {
    return {
      progressRankPercent: (userExperience / firstRequirement) * 100,
      maxExperienceRank: firstRequirement,
    };
  }

  if (userExperience >= requirementLastRankLastLevel) {
    return {
      progressRankPercent: userExperience,
      maxExperienceRank: null,
    };
  }

  let result: getProgressOnRankResult = {
    progressRankPercent: userExperience,
    maxExperienceRank: null,
  };

  loyalty.settings.ranks.forEach((rank, currentIndexRank, ranks) => {
    const { levels } = rank;

    const countLevelsOnRank = levels.length;
    const nextRankExperience = ranks?.[currentIndexRank + 1]
      ? ranks[currentIndexRank + 1].levels[0].requirement
      : null;
    const lastLevelExperience = levels[countLevelsOnRank - 1].requirement;
    const requirementMin = levels[0].requirement;
    const requirementMax = nextRankExperience || lastLevelExperience;

    if (userExperience >= requirementMin && userExperience < requirementMax) {
      const progressRankPercent =
        ((userExperience - requirementMin) /
          (requirementMax - requirementMin)) *
        100;
      result = {
        progressRankPercent,
        maxExperienceRank: requirementMax,
      };
    }
  });

  return result;
};

export const getIdsRewardsOnRank = (
  loyalty?: Loyalty,
  currencyCode?: string,
) => {
  const ids: string[] = [];

  if (!loyalty || !currencyCode) {
    return ids;
  }

  const getRewards = (
    rewards: {
      rewardNominalId?: string;
      rewardDepositWeeklyId?: string;
      rewardCashbackDailyId?: string;
      rewardCashbackWeeklyId?: string;
      rewardCashbackMonthlyId?: string;
      currencyRewards: CurrencyRewardLoyalty[];
    },
    key: string,
  ) => {
    const value = rewards[key];

    if (Array.isArray(value)) {
      const currencyRewards = value.find(
        (val: CurrencyRewardLoyalty) => val.currencyCode === currencyCode,
      );

      if (currencyRewards) {
        const keysCurrencyReward = Object.keys(currencyRewards.currencyReward);
        keysCurrencyReward.forEach((keyCurrencyReward) => {
          if (KEYS_REWARD_BONUS_ECOSYSTEM.includes(keyCurrencyReward)) {
            ids.push(currencyRewards.currencyReward[keyCurrencyReward]);
          }
        });
      }
    }

    if (KEYS_REWARD_BONUS_ECOSYSTEM.includes(key)) {
      ids.push(rewards[key]);
    }
  };

  loyalty.settings.ranks.forEach((rank) => {
    const rewardsRank = rank.rewards;
    const keysRewardsRank = Object.keys(rewardsRank);

    keysRewardsRank.forEach((keyRewardsRank) =>
      getRewards(rewardsRank, keyRewardsRank),
    );

    rank.levels.forEach((level) => {
      const rewardsLevel = level.rewards;
      const keysRewardsLevel = Object.keys(rewardsLevel);

      keysRewardsLevel.forEach((keyRewardLevel) =>
        getRewards(rewardsLevel, keyRewardLevel),
      );
    });
  });

  return ids;
};

type FormatDataProgressLoyaltyRanksParams = {
  t: ReturnType<typeof useTranslate>;
  loyalty?: Loyalty;
  userExperience?: number;
  userRewards?: LoyaltyRewardUser[];
  infoRewards?: Array<RewardFreeSpins | RewardBonusNominal>;
  currencyCode?: string;
  boostersInfo?: BoosterBackend[];
};

export const formatDataProgressLoyaltyRanks = (
  params: FormatDataProgressLoyaltyRanksParams,
): Array<{
  name: string;
  rewards: RewardItemT[];
  statusRank: LoyaltyTagStatusVariantT;
  levels: Array<{
    title: string;
    status: StatusRank;
    rewards: RewardItemT[];
    requirement: number;
    nextRequirement: number;
  }>;
}> => {
  const { loyalty, userExperience, userRewards, infoRewards, currencyCode, t } =
    params;

  if (
    !loyalty ||
    userExperience === undefined ||
    userExperience === null ||
    !userRewards ||
    !infoRewards ||
    !currencyCode
  ) {
    return [];
  }

  const getValueRewardBonusEcosystem = (id: string) => {
    const reward = infoRewards.find(
      (infoReward) => infoReward.reward.id === id,
    );

    if (reward) {
      if ("rewardFreeSpin" in reward) {
        return `${reward.rewardFreeSpin.freeSpinsCount} FS`;
      }
      if ("rewardNominal" in reward) {
        const val = reward.rewardNominal.amount.find(
          (am) => am.currency === currencyCode,
        )?.value;
        return val ? `${val / 100} ${currencyCode}` : undefined;
      }
    }

    return undefined;
  };

  return loyalty.settings.ranks.map((rank, indexRank, arrRanks) => {
    const rankIsReached = userExperience >= rank?.levels?.[0]?.requirement;
    const levelsFormated = rank.levels.map((level, indexLevel, arrLevels) => {
      const infoRank: InfoRank = {
        indexRank,
        indexLevel: Number(level.name),
      };
      const levelIsReached = userExperience >= level.requirement;
      const rewardsKeys = Object.keys(level.rewards);
      const rewards: RewardItemT[] = [];
      const statusLevel: StatusRank = levelIsReached ? "progress" : "close";

      rewardsKeys.forEach((rewardKey) => {
        const value: CurrencyRewardLoyalty[] | string =
          level.rewards[rewardKey];

        if (Array.isArray(value)) {
          const currencyRewards = value.find(
            (val) => val.currencyCode === currencyCode,
          );

          if (currencyRewards) {
            const keysCurrencyReward = Object.keys(
              currencyRewards.currencyReward,
            );

            keysCurrencyReward.forEach((keyCurrencyReward) => {
              if (KEYS_REWARD_BONUS_ECOSYSTEM.includes(keyCurrencyReward)) {
                const idReward = currencyRewards.currencyReward[
                  keyCurrencyReward
                ] as string;

                const targetReward = userRewards.find(
                  (reward) =>
                    reward.rewardId === idReward &&
                    reward.status === LoyaltyRewardUserStatus.Assigned &&
                    reward.levelIndex === infoRank.indexLevel &&
                    reward.rankIndex === infoRank.indexRank,
                );

                rewards.push({
                  name: keyCurrencyReward as RewardNameT,
                  value: getValueRewardBonusEcosystem(
                    currencyRewards.currencyReward[keyCurrencyReward],
                  ),
                  isClose: !levelIsReached || !targetReward,
                });
                return;
              }

              rewards.push({
                name: keyCurrencyReward as RewardNameT,
                isClose: !levelIsReached,
              });
            });
          }

          return;
        }

        if (KEYS_REWARD_BONUS_ECOSYSTEM.includes(rewardKey)) {
          const userRewardIsFind = userRewards.find(
            (userReward) =>
              userReward.rewardId === value &&
              userReward.rankIndex === infoRank.indexRank &&
              userReward.levelIndex === infoRank.indexLevel &&
              userReward.status === LoyaltyRewardUserStatus.Assigned,
          );

          rewards.push({
            name: rewardKey as RewardNameT,
            value: getValueRewardBonusEcosystem(value),
            isClose: !levelIsReached || !userRewardIsFind,
          });

          return;
        }

        rewards.push({
          name: rewardKey as RewardNameT,

          isClose: !levelIsReached,
        });
      });

      // eslint-disable-next-line no-nested-ternary
      const nextRequirement = arrLevels[indexLevel + 1]
        ? arrLevels[indexLevel + 1].requirement
        : arrRanks[indexRank + 1]
          ? arrRanks[indexRank + 1].levels[0].requirement
          : level.requirement + 100;

      return {
        rewards,
        title: `${t(rank.name)} ${level.name}`,
        status: (statusLevel === "progress" &&
        rewards.every((reward) => !reward.isClose)
          ? "complete"
          : statusLevel) as StatusRank,
        requirement: level.requirement,
        nextRequirement,
      };
    });

    const rewardsRank: RewardItemT[] = [];
    const keysRewardsRank = Object.keys(rank.rewards);
    const infoRank: InfoRank = {
      indexRank,
      indexLevel: undefined,
    };

    keysRewardsRank.forEach((keyRewardsRank) => {
      const value: CurrencyRewardLoyalty[] | string =
        rank.rewards[keyRewardsRank];
      if (Array.isArray(value)) {
        const currencyRewards = value.find(
          (val) => val.currencyCode === currencyCode,
        );

        if (currencyRewards?.currencyReward) {
          const keysCurrencyRewards = Object.keys(
            currencyRewards?.currencyReward,
          );

          keysCurrencyRewards.forEach((keyCurrencyReward) => {
            if (KEYS_REWARD_BONUS_ECOSYSTEM.includes(keyCurrencyReward)) {
              rewardsRank.push({
                name: keyCurrencyReward as RewardNameT,
                value: getValueRewardBonusEcosystem(
                  currencyRewards?.currencyReward[keyCurrencyReward],
                ),
                isClose:
                  !rankIsReached ||
                  !userRewards.find(
                    (reward) =>
                      reward.rewardId ===
                        currencyRewards.currencyReward[keyCurrencyReward] &&
                      reward.status === LoyaltyRewardUserStatus.Assigned &&
                      infoRank.indexRank === reward.rankIndex &&
                      infoRank.indexLevel === reward.levelIndex,
                  ),
              });
              return;
            }

            rewardsRank.push({
              name: keyCurrencyReward as RewardNameT,
              isClose: !rankIsReached,
            });
          });
        }

        return;
      }

      if (KEYS_REWARD_BONUS_ECOSYSTEM.includes(keyRewardsRank)) {
        rewardsRank.push({
          name: keyRewardsRank as RewardNameT,
          value: getValueRewardBonusEcosystem(value),
          isClose:
            !rankIsReached ||
            !userRewards.find(
              (reward) =>
                reward.rewardId === value &&
                reward.status === LoyaltyRewardUserStatus.Assigned &&
                infoRank.indexRank === reward.rankIndex &&
                infoRank.indexLevel === reward.levelIndex,
            ),
        });
        return;
      }

      rewardsRank.push({
        name: keyRewardsRank as RewardNameT,
        isClose: !rankIsReached,
      });
    });

    const getStatus = (): LoyaltyTagStatusVariantT => {
      if (!rankIsReached) {
        return "nextRank";
      }

      const levelsGettingReward = levelsFormated
        .map((level) => level.rewards.map((reward) => reward.isClose))
        .flat();

      if (levelsGettingReward.every((isClose) => !isClose)) {
        return "collected";
      }

      return "progress";
    };

    return {
      name: rank.name,
      levels: levelsFormated,
      rewards: rewardsRank,
      statusRank: getStatus(),
    };
  });
};

export const getNameRankMascot = (
  loyalty?: Loyalty,
  activeMascotId?: string,
  loyaltyMascots?: ResponseLoyaltyMascotInner[],
  currency?: string,
): string => {
  if (!loyalty || !activeMascotId || !loyaltyMascots || !currency) {
    return "";
  }

  const findRank =
    (loyalty?.settings?.ranks.find((rank) => {
      const rewardsRankCurrency = rank.rewards.currencyRewards.find(
        (rewardCur) => rewardCur.currencyCode === currency,
      );

      if (rewardsRankCurrency?.currencyReward.mascotId === activeMascotId) {
        return true;
      }

      let innerNameRank: string | null = null;

      rank.levels.forEach((level) => {
        const rewards = level.rewards.currencyRewards.find(
          (levelCurRew) => levelCurRew.currencyCode === currency,
        );

        if (rewards && rewards.currencyReward.mascotId === activeMascotId) {
          innerNameRank = rank.name;
        }
      });

      return !!innerNameRank;
    }) as RankLoyalty) || undefined;

  return findRank ? findRank?.name : "";
};

export const mapRanksToCard = (
  ranks: RankBFF[] = [],
  userExperience: number = 0,
): LoyaltyLevelRewardButtonProps[] => {
  if (ranks.length === 0) {
    return [];
  }

  return ranks.map((rank, index, arr) => {
    const { name, levels } = rank;
    const startRequirement = levels[0].requirement;
    const endRequirement = arr[index + 1]
      ? arr[index + 1].levels[0].requirement
      : levels[rank.levels.length - 1].requirement;

    const getVariant = (): LoyaltyLevelRewardButtonVariant => {
      switch (true) {
        case userExperience >= startRequirement &&
          userExperience < endRequirement: {
          return "current";
        }
        case userExperience < startRequirement: {
          return "next";
        }
        case userExperience > startRequirement: {
          return "prev";
        }
        default: {
          return "next";
        }
      }
    };

    return {
      nameRank: name,
      indexRank: index,
      active: false,
      userExperience,
      variant: getVariant(),
      startRequirementRank: startRequirement,
      endRequirementRank: endRequirement,
      handleClick: () => {},
    };
  });
};

export const getChestLoyalty = (
  indexRank?: number,
  level?: LevelBFF,
  allRewardsIsGetting?: boolean,
  userExperience?: number,
) => {
  if (level === undefined && indexRank !== undefined) {
    return RANK_LEVEL_LOYALTY_IMAGES[indexRank]?.rank || "";
  }

  if (
    level === undefined ||
    allRewardsIsGetting === undefined ||
    userExperience === undefined ||
    indexRank === undefined
  ) {
    return "";
  }

  const { name } = level;

  let key = "";
  const indexLevel = name >= 3 ? 3 : name;

  if (allRewardsIsGetting) {
    key = "empty";
  } else if (userExperience >= level.requirement) {
    key = "progress";
  } else {
    key = "close";
  }
  const resultKey = `${key}${indexLevel}`;

  return RANK_LEVEL_LOYALTY_IMAGES[indexRank][resultKey];
};
